import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredArticles } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheets.boutique';

const setupJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${siteUrl}/mulebuy-spreadsheet-setup-guide#article`,
  headline: 'Mulebuy Spreadsheet Setup: Step-by-Step Guide for Beginners',
  description: 'A detailed step-by-step tutorial covering Mulebuy Spreadsheet setup from template selection through automated calculations and tracking dashboards for beginners in 2026.',
  image: 'https://readdy.ai/api/search-image?query=Step%20by%20step%20setup%20guide%20scene%20with%20laptop%20showing%20configuration%20wizard%20and%20notebook%20with%20handwritten%20instructions%2C%20bright%20desk%20with%20colorful%20sticky%20notes%2C%20organized%20tutorial%20atmosphere%2C%20warm%20morning%20light%2C%20friendly%20beginner-friendly%20photography&width=1200&height=600&seq=setup-hero-v2&orientation=landscape',
  datePublished: '2026-06-22',
  dateModified: '2026-06-22',
  author: {
    '@type': 'Organization',
    name: 'Mulebuy Spreadsheet Hub',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Mulebuy Spreadsheet Hub',
    url: siteUrl,
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/mulebuy-spreadsheet-setup-guide`,
  },
  inLanguage: 'en',
  isAccessibleForFree: true,
};

export default function SetupGuideArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(setupJsonLd) }}
      />
      <Navbar />
      <main>
        <article className="pt-24 section-padding">
          <div className="container-custom max-w-3xl">
            <nav className="mb-6">
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-accent-500 transition-colors font-label">
                <i className="ri-arrow-left-line"></i> Back to Home
              </Link>
            </nav>

            <header className="mb-10">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold font-label mb-4">
                Setup Tutorial
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight">
                Mulebuy Spreadsheet Setup: <span className="italic text-accent-500">Step-by-Step Guide</span> for Beginners
              </h1>
              <p className="text-sm text-foreground-500 mt-4">Updated: June 22, 2026 &middot; 12 min read</p>
            </header>

            <div className="rounded-2xl overflow-hidden mb-10">
              <img src="https://readdy.ai/api/search-image?query=Step%20by%20step%20setup%20guide%20scene%20with%20laptop%20showing%20configuration%20wizard%20and%20notebook%20with%20handwritten%20instructions%2C%20bright%20desk%20with%20colorful%20sticky%20notes%2C%20organized%20tutorial%20atmosphere%2C%20warm%20morning%20light%2C%20friendly%20beginner-friendly%20photography&width=1200&height=600&seq=setup-hero-v2&orientation=landscape" alt="Mulebuy Spreadsheet setup tutorial" className="w-full object-cover" />
            </div>

            <div className="prose-custom">
              <p className="text-base text-foreground-700 leading-relaxed mb-6">
                Setting up your <strong className="text-foreground-900">Mulebuy Spreadsheet</strong> correctly from day one saves hours of rework later. This step-by-step tutorial walks you through the entire Mulebuy Spreadsheet configuration process — from initial template selection to advanced formula integration — so you can start managing data with confidence.
              </p>

              <h2 className="text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Step 1: Choose Your Mulebuy Spreadsheet Template</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">Begin by selecting a Mulebuy Spreadsheet template that matches your workflow. Templates are pre-structured with categories, calculation formulas, and tracking fields — giving you a solid foundation to customize. Whether you manage products, orders, or sourcing data, there&apos;s a Mulebuy Spreadsheet template designed for your needs.</p>

              <h2 className="text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Step 2: Configure Data Input Fields</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">Define the essential columns in your Mulebuy Spreadsheet — product names, supplier details, cost prices, and inventory counts. Consistency in data entry is the hallmark of a well-functioning Mulebuy Spreadsheet. Use dropdown validation rules to prevent typos and maintain data integrity across thousands of entries.</p>

              <h2 className="text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Step 3: Set Up Automated Calculations</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">Your Mulebuy Spreadsheet becomes powerful when formulas automate repetitive calculations. Set up profit margin formulas, shipping cost estimators, and pricing calculators that update automatically as you input new data. This is where your Mulebuy Spreadsheet transforms from a simple table into a dynamic business intelligence tool.</p>

              <h2 className="text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Step 4: Build Tracking Dashboards</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">Create visual tracking dashboards within your Mulebuy Spreadsheet to monitor key metrics at a glance. Order status, delivery timelines, and inventory levels become instantly visible — turning your Mulebuy Spreadsheet into a real-time command center for daily operations.</p>

              <h2 className="text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">Step 5: Test and Optimize</h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-4">Run sample data through your completed Mulebuy Spreadsheet setup to identify any gaps or errors. The best Mulebuy Spreadsheet users treat their system as a living document — continuously refining formulas, adding new tracking metrics, and optimizing workflows as their business evolves.</p>
            </div>

            <hr className="border-background-200 my-12" />
            <div className="mb-12">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">More Mulebuy Spreadsheet Resources</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {featuredArticles.filter(a => a.seq !== 'article-03').slice(0, 6).map((article) => (
                  <Link key={article.seq} to={article.slug} className="flex items-start gap-3 p-4 rounded-xl bg-background-100 hover:bg-background-200/60 transition-all group">
                    <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                      <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 font-label leading-snug group-hover:text-accent-600 transition-colors">{article.title}</h4>
                      <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-accent-600 font-label">Read <i className="ri-arrow-right-line text-[10px]"></i></span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 p-6 rounded-xl bg-primary-900 text-background-50">
              <div className="flex-1">
                <h3 className="text-lg font-heading font-bold">Start building your Mulebuy Spreadsheet today</h3>
                <p className="text-sm text-background-300 mt-1">Access the full platform with all the tools and templates you need.</p>
              </div>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto px-6 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap font-label flex items-center justify-center gap-2">
                Visit Main Platform <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <div className="mt-8 text-center">
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-accent-500 transition-colors font-label">
                <i className="ri-arrow-left-line"></i> Back to Home
              </Link>
            </div>
          </div>
        </article>
        <style>{`.prose-custom h2{scroll-margin-top:5rem}.prose-custom strong{color:oklch(var(--foreground-900))}`}</style>
      </main>
      <Footer />
    </div>
  );
}